import { Box, Card, CardContent, Chip, Typography } from '@mui/material'
import type { FC } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import MaterialChips from './MaterialChips'
import WoodworkPhoto from './WoodworkPhoto'

type ProjectCardProps = {
  readonly title: string
  readonly description: string
  readonly materials?: readonly string[]
  readonly status?: string
  readonly date?: string
  readonly tag?: string
  readonly to?: string
  readonly placeholderLabel?: string
  readonly imageSrc?: string
  readonly imageAlt?: string
}

const ProjectCard: FC<ProjectCardProps> = ({
  title,
  description,
  materials = [],
  status,
  date,
  tag,
  to,
  placeholderLabel = 'Photo coming soon',
  imageSrc,
  imageAlt
}) => {
  const inner = (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.08)',
        transition: 'border-color 0.2s ease, transform 0.2s ease',
        ...(to && {
          cursor: 'pointer',
          textDecoration: 'none',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-2px)'
          }
        })
      }}
    >
      {imageSrc ? (
        <WoodworkPhoto
          layout='card'
          src={imageSrc}
          alt={imageAlt ?? title}
          loading='lazy'
        />
      ) : (
        <Box className='placeholder-media' sx={{ borderRadius: 0, border: 'none' }}>
          <span className='placeholder-media__label'>{placeholderLabel}</span>
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {status && (
            <Chip
              label={status}
              size='small'
              sx={{
                textTransform: 'capitalize',
                letterSpacing: '0.04em',
                fontSize: '0.7rem',
                borderColor: 'rgba(201, 162, 39, 0.35)',
                color: 'primary.light'
              }}
              variant='outlined'
            />
          )}
          {tag && (
            <Chip
              label={tag}
              size='small'
              sx={{ letterSpacing: '0.04em', fontSize: '0.7rem' }}
              color='secondary'
              variant='outlined'
            />
          )}
          {date && (
            <Typography
              component='span'
              variant='caption'
              sx={{
                alignSelf: 'center',
                color: 'text.secondary',
                letterSpacing: '0.06em',
                fontSize: '0.7rem'
              }}
            >
              {date}
            </Typography>
          )}
        </Box>
        <Typography
          variant='h6'
          component='h3'
          sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 500 }}
        >
          {title}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.7 }}>
          {description}
        </Typography>
        <MaterialChips materials={materials} />
        {to ? (
          <Typography
            variant='caption'
            sx={{ mt: 'auto', pt: 0.5, color: 'primary.main', letterSpacing: '0.08em' }}
          >
            View gallery →
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  )

  if (to) {
    return (
      <Box component={RouterLink} to={to} sx={{ textDecoration: 'none', color: 'inherit' }}>
        {inner}
      </Box>
    )
  }

  return inner
}

export default ProjectCard
